import { JSX, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex } from '@chakra-ui/react'
import { RequestReceivedEmail, RequestReveicedEmailAccessoryData } from '@leid/api-orders'
import { getValidTranslation } from '@leid/api-products'
import { tryParseAsNumber } from '@leid/helpers'
import { useFormik } from 'formik'
import { useRouter } from 'next/navigation'
import * as Yup from 'yup'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { CartFormValues } from 'appTypes/orders'
import { Input } from 'components/form/Input'
import { Button, ButtonSecondary } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { AppDispatch } from 'store'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

type Props = {
  cartFormValues: CartFormValues
  goToPrevStep?: VoidFunction
  product: ItemApi
  disabled?: boolean
  customerInfo: CustomerInfo
}

type QuotationFormData = {
  email?: string
  phoneNumber?: string
  fullName?: string
}

const SendServiceRequestForm = (props: Props): JSX.Element => {
  const { cartFormValues, goToPrevStep, product, disabled, customerInfo } = props

  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation(['auth', 'common', 'pageTitles', 'requests'])

  const router = useRouter()

  const isAuthenticated = customerInfo.customer !== null

  const [isSubmitting, setIsSubmitting] = useState(false)

  const onActualSubmit = async (formValues?: QuotationFormData) => {
    setIsSubmitting(true)

    if (!cartFormValues.selectedLocationId) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    const locationsResponse = await apiClient.getLocations({
      ids: [cartFormValues.selectedLocationId],
    })
    if (!locationsResponse.success || !locationsResponse.data || locationsResponse.data.length === 0) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    const location = locationsResponse.data[0]

    const accessories: RequestReveicedEmailAccessoryData[] = []

    for (const [key, amount] of Object.entries(cartFormValues.complementaryItemsQuantity ?? [])) {
      const productId = tryParseAsNumber(key) ?? 0

      const productResponse = await apiClient.getProduct(productId)
      if (!productResponse.success || !productResponse.data) {
        dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
        setIsSubmitting(false)
        return
      }

      const product = productResponse.data

      accessories.push({
        productId: tryParseAsNumber(key) ?? 0,
        productName: getValidTranslation('', product.nameTranslations)?.translation ?? '',
        amount: amount,
      })
    }

    const request: RequestReceivedEmail = {
      phoneNumber: customerInfo?.customer?.phoneNumber ?? formValues?.phoneNumber ?? '',
      fullName: customerInfo?.customer?.fullName ?? formValues?.fullName ?? '',
      productId: product.id,
      productName: product.name,
      locationId: cartFormValues.selectedLocationId,
      rentedFrom: buildDate(cartFormValues.startDate, cartFormValues.pickupAt),
      rentedTo: buildDate(cartFormValues.endDate, cartFormValues.returnAt),
      managerId: location.managerId ?? 0,
      accessories: accessories,
    }

    const response = await apiClient.sendOrderRequestReceivedEmail(request)
    if (!response.success || !response.data) {
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      setIsSubmitting(false)
      return
    }

    dispatch(displaySnackbar({ message: t('common:requestWasSent'), isError: false }))
    router.push('/browse')
  }

  const { getFieldProps, touched, errors, handleSubmit } = useFormik<QuotationFormData>({
    initialValues: {
      email: '',
      phoneNumber: '',
      fullName: '',
    },
    onSubmit: async (formValues) => {
      onActualSubmit(formValues)
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('common:invalidEmail')).required(t('common:required')),
      phoneNumber: Yup.number()
        .test('len', t('common:phoneNumberValidation'), (val) => (val ? val.toString().length === 8 : false))
        .required(t('common:required'))
        .typeError(t('common:validationNumber')),
      fullName: Yup.string().required(t('common:required')),
    }),
    validateOnMount: true,
  })

  const onSumbit = () => {
    if (!isAuthenticated) {
      // Let formik to do the job
      handleSubmit()
      return
    }

    onActualSubmit()
  }

  return (
    <>
      <Box padding={isAuthenticated ? 0 : 5}>
        {!isAuthenticated && (
          <Box marginTop="36px">
            <Box marginTop="20px">
              <Input
                {...getFieldProps('email')}
                placeholder={t('common:email')}
                name="email"
                touched={touched.email}
                error={errors.email}
                label={t('common:email')}
              />
            </Box>
            <Box marginTop="20px">
              <Input
                {...getFieldProps('phoneNumber')}
                placeholder={t('common:phoneNumber')}
                name="phoneNumber"
                touched={touched.phoneNumber}
                error={errors.phoneNumber}
                type="number"
                label={t('common:phoneNumber')}
                adornmentLeft="+47"
                paddingLeft="35px"
              />
            </Box>
            <Box marginTop="20px">
              <Input
                {...getFieldProps('c')}
                placeholder={t('common:fullName')}
                name="fullName"
                touched={touched.fullName}
                error={errors.fullName}
                label={t('common:fullName')}
              />
            </Box>
          </Box>
        )}

        {isSubmitting ? (
          <Loader />
        ) : (
          <>
            <Flex marginTop={isAuthenticated ? 0 : '20px'}>
              {goToPrevStep && (
                <Box flex={1} maxWidth={{ lg: '200px' }}>
                  <ButtonSecondary onClick={goToPrevStep}>{t('common:editRentalPeriod')}</ButtonSecondary>
                </Box>
              )}
              <Box flex={1} marginLeft="10px">
                <Button onClick={onSumbit} isDisabled={disabled === true}>
                  {t('common:requestQuotationButton')}
                </Button>
              </Box>
            </Flex>
          </>
        )}
      </Box>
    </>
  )
}

export default SendServiceRequestForm
